/*
 * Purpose: Live running flows — front-and-center dashboard hero
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Activity, Map, Square } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useExecutionRuntimeStore, type RunningFlowRecord } from '@/stores/executionRuntimeStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { missionMapHref } from '@/lib/appRoutes';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { toastContextualIssue } from '@/lib/contextualIssuePolicy';
import { toast } from 'sonner';

function RunningFlowCard({ record }: { record: RunningFlowRecord }) {
  const navigate = useNavigate();
  const stopRun = useExecutionRuntimeStore((s) => s.stopRun);
  const setCurrentWorkflow = useWorkflowStore((s) => s.setCurrentWorkflow);
  const workflows = useWorkflowStore((s) => s.workflows);
  const [, tick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const started = new Date(record.startedAt);
  const elapsedSec = Math.max(0, Math.floor((Date.now() - started.getTime()) / 1000));

  const openMission = () => {
    const wf = workflows.find((w) => w.id === record.workflowId);
    if (wf) setCurrentWorkflow(wf);
    navigate(missionMapHref(record.workflowId));
  };

  const handleStop = () => {
    if (stopRun(record.workflowId)) {
      toast.message(`${record.workflowName} stopped`);
    } else {
      toastContextualIssue('Could not stop from here — open the Mission map to stop the run.', {
        label: `Open ${PRODUCT_TERMS.mission}`,
        onClick: openMission,
      });
    }
  };

  return (
    <Card className="p-4 sm:p-5 border-[hsl(var(--ab-green-core)/0.35)] bg-[hsl(var(--ab-green-core)/0.06)] backdrop-blur-sm shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--ab-green-core)/0.15)]">
            <Activity className="h-5 w-5 text-[hsl(var(--ab-green-deep))] dark:text-[hsl(var(--ab-green-glow))]" />
            <span className="absolute inset-0 rounded-full animate-ping bg-[hsl(var(--ab-green-glow)/0.25)]" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-foreground truncate">{record.workflowName}</h3>
              <Badge className="bg-[hsl(var(--ab-green-core))] text-primary-foreground shrink-0">
                Live
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Running {elapsedSec}s · {PRODUCT_TERMS.mission} in progress
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button size="sm" variant="destructive" onClick={handleStop} className="ui-lift-subtle">
            <Square className="h-4 w-4 mr-1 fill-current" />
            Stop
          </Button>
          <Button size="sm" actionTier="secondary" onClick={openMission} className="ui-lift-subtle">
            <Map className="h-4 w-4 mr-1" />
            Open {PRODUCT_TERMS.mission}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function LiveRunningFlowsPanel() {
  const running = useExecutionRuntimeStore((s) => s.running);

  if (running.length === 0) {
    return (
      <Card className="p-6 sm:p-8 border-dashed border-border/80 bg-muted/20 text-center">
        <Activity className="h-8 w-8 mx-auto text-muted-foreground/60 mb-3" />
        <p className="text-sm font-medium text-foreground">No flows running right now</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
          Start a validated flow from the Mission map — live runs appear here with stop controls.
        </p>
        <Button asChild size="sm" variant="outline" className="mt-4">
          <Link to={REMEDIATION.mission}>Open {PRODUCT_TERMS.mission}</Link>
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {running.map((record) => (
        <RunningFlowCard key={record.workflowId} record={record} />
      ))}
    </div>
  );
}

export default LiveRunningFlowsPanel;
